import { Button, Group, Loader, Stack, Text, Textarea } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import { z } from 'zod';

import { useAdminRetrieveBatchLRNs, useReassignDeclarations } from '@admin/hooks';
import { TextInput } from '@components';

interface ReassignDeclarationsProps {
  onClose: () => void;
  batchCounter: number;
}

const reassignSchema = z.object({
  lrns: z.string().min(1, { message: 'LRNs are required' }),
  destinationBatchCounter: z.string().refine((val) => !!val && !isNaN(Number(val)) && Number(val) > 0, { message: 'Destination batch counter must be number' }),
});

export const ReassignDeclarations: React.FC<ReassignDeclarationsProps> = ({ onClose, batchCounter }) => {
  const reassignDeclarationsMutation = useReassignDeclarations();
  const { isLoading: isLoadingLRNs, refetch: fetchBatchLRNs, error: fetchError } = useAdminRetrieveBatchLRNs(batchCounter);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      lrns: '',
      destinationBatchCounter: '',
    },
    validate: zodResolver(reassignSchema),
    validateInputOnBlur: true,
  });

  const handleSubmit = async (values: typeof form.values) => {
    setFormError(null);
    try {
      await reassignDeclarationsMutation.mutateAsync({ lrns: values.lrns, destinationBatchCounter: Number(values.destinationBatchCounter) });
      onClose();
    } catch (error: any) {
      setFormError(error?.response?.data?.message || 'Failed to reassign declarations');
    }
  };

  const handleRetrieveLRNs = async () => {
    setFormError(null);
    const result = await fetchBatchLRNs();

    if (result.error) {
      setFormError((result.error as any)?.response?.data?.message || 'Failed to retrieve LRNs');
      return;
    }
    form.setFieldValue('lrns', result.data.join(',\n'));
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Text size="lg" fw={500}>
            LRN list
          </Text>
          <Button onClick={handleRetrieveLRNs} disabled={isLoadingLRNs} size="sm">
            Retrieve Batch LRNs
          </Button>
        </Group>

        <Textarea {...form.getInputProps('lrns')} minRows={7} autosize maxRows={7} />
        <TextInput label="Destination Batch Counter" type="number" {...form.getInputProps('destinationBatchCounter')} />
        {formError && (
          <Text c="red" fw="bold">
            {formError}
          </Text>
        )}
        <Group justify="center" mt="md">
          <Button type="button" onClick={onClose} disabled={reassignDeclarationsMutation.isPending} variant="transparent">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={reassignDeclarationsMutation.isPending || !form.isValid()}
            leftSection={reassignDeclarationsMutation.isPending && <Loader color="white" />}
          >
            Reassign
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
